import { createRouter, createWebHistory } from 'vue-router'
import store from '../store'

// Views
import StationSelector from '../views/StationSelector.vue'
import StationEditor from '../views/StationEditor.vue'
import LoginView from '../views/LoginView.vue'
import AcceptInviteView from '../views/AcceptInviteView.vue'
import ChangePasswordView from '../views/ChangePasswordView.vue'
import ForgotPasswordView from '../views/ForgotPasswordView.vue'
import ResetPasswordView from '../views/ResetPasswordView.vue'
import AdminUsersView from '../views/AdminUsersView.vue'
import StationGroupsView from '../views/StationGroupsView.vue'
import ExportsView from '../views/ExportsView.vue'
import SocialVideoView from '../views/SocialVideoView.vue'
import VoicesView from '../views/VoicesView.vue'
import TypeCategoriesView from '../views/TypeCategoriesView.vue'
import StatsView from '../views/StatsView.vue'
import CompareStationsView from '../views/CompareStationsView.vue'
import SettingsView from '../views/SettingsView.vue'

const routes = [
  // Public routes
  {
    path: '/login',
    name: 'Login',
    component: LoginView,
    meta: { public: true, guest: true }
  },
  {
    path: '/invite/:token',
    name: 'AcceptInvite',
    component: AcceptInviteView,
    meta: { public: true }
  },
  {
    path: '/forgot-password',
    name: 'ForgotPassword',
    component: ForgotPasswordView,
    meta: { public: true, guest: true }
  },
  {
    path: '/reset-password/:token',
    name: 'ResetPassword',
    component: ResetPasswordView,
    meta: { public: true }
  },
  
  // Auth required routes
  {
    path: '/change-password',
    name: 'ChangePassword',
    component: ChangePasswordView,
    meta: { auth: true }
  },
  {
    path: '/',
    redirect: '/stations'
  },
  {
    path: '/stations',
    name: 'Stations',
    component: StationSelector,
    meta: { auth: true }
  },
  {
    path: '/station/:slug',
    name: 'Station',
    component: StationEditor,
    props: true,
    meta: { auth: true }
  },
  {
    path: '/station/:slug/exports',
    name: 'Exports',
    component: ExportsView,
    meta: { auth: true }
  },
  {
    path: '/station/:slug/voices',
    name: 'Voices',
    component: VoicesView,
    props: true,
    meta: { auth: true },
    beforeEnter: async (to, from, next) => {
      try {
        const store = (await import('../store')).default
        const token = store.getters['auth/accessToken']
        const res = await fetch('/api/features', { headers: { Authorization: `Bearer ${token}` } })
        const data = await res.json()
        if (data.voices === false) return next('/stations')
      } catch (e) { /* ignore */ }
      next()
    }
  },
  {
    path: '/station/:slug/exports/:exportId/social-video',
    name: 'SocialVideo',
    component: SocialVideoView,
    meta: { auth: true },
    beforeEnter: (to, from, next) => {
      if (process.env.VUE_APP_SOCIAL_VIDEO === 'false') return next('/stations')
      next()
    }
  },
  {
    path: '/station/:slug/stats',
    name: 'Stats',
    component: StatsView,
    props: true,
    meta: { auth: true }
  },
  {
    path: '/station/:slug/type-categories',
    name: 'TypeCategories',
    component: TypeCategoriesView,
    props: true,
    meta: { auth: true, admin: true }
  },
  {
    path: '/compare',
    name: 'CompareStations',
    component: CompareStationsView,
    meta: { auth: true }
  },
  
  // Admin routes
  {
    path: '/admin/users',
    name: 'AdminUsers',
    component: AdminUsersView,
    meta: { auth: true, admin: true }
  },
  {
    path: '/station-groups',
    name: 'StationGroups',
    component: StationGroupsView,
    meta: { auth: true, admin: true }
  },
  {
    path: '/settings',
    name: 'Settings',
    component: SettingsView,
    meta: { auth: true, admin: true }
  }
]

const router = createRouter({
  history: createWebHistory(process.env.BASE_URL),
  routes,
  scrollBehavior() {
    return { top: 0 }
  }
})

// Navigation guards
router.beforeEach(async (to, from, next) => {
  // Public routes - allow access
  if (to.meta.public) {
    // If logged in and trying to access guest-only pages (like login), redirect
    if (to.meta.guest && store.getters['auth/isAuthenticated']) {
      return next('/stations')
    }
    return next()
  }

  // Check if authenticated
  if (!store.getters['auth/isAuthenticated']) {
    // Try to restore session
    const restored = await store.dispatch('auth/tryRestoreSession')
    if (!restored) {
      return next('/login')
    }
  }

  // Check if password change is required
  const user = store.getters['auth/user']
  if (user && user.mustChangePassword && to.path !== '/change-password') {
    return next('/change-password')
  }

  // Admin-only routes
  if (to.meta.admin && user.role !== 'admin') {
    return next('/stations')
  }

  // Station access check
  if (to.params.slug && user.role !== 'admin') {
    // Get station to check access
    // For now we'll let the API handle this check
  }

  next()
})

export default router
